<template>
  <div class="accordion-container prompts-container">
    <div class="d-flex flex-column mt-4">
      <!-- Jail Calls Section -->
      <div class="text-h6 font-weight-bold" style="color: #424242;">
        When a Jail call comes in:
      </div>
      <div class="radio-group mt-4 ms-8">
        <v-radio-group 
          v-model="jailCallHandling"
          @update:model-value="handleJailCallHandlingChange"
        >
          <v-radio value="forward">
            <template v-slot:label>
              <div class="d-flex align-items-center" style="font-size: 17px; font-weight: bold; color: #424242;">
                Answer the jail call preamble then forward the call to this number:
              </div>
            </template>
          </v-radio>
          <div class="ms-8 mt-2 mb-4 d-flex align-center" :class="{ 'opacity-50': jailCallHandling !== 'forward' }">
            <!-- View Mode -->
            <div v-if="!isEditingJailForwarding" class="d-flex align-center">
              <div class="uneditable-number">{{ jailForwardingNumber || 'No number set' }}</div>
              <v-icon 
                class="ms-2 edit-icon" 
                size="small"
                @click="startJailCallForwardingEdit"
              >
                mdi-pencil
              </v-icon>
            </div>

            <!-- Edit Mode -->
            <div v-else class="d-flex align-center">
              <v-text-field
                v-model="jailEditingNumber"
                class="forwarding-input"
                density="compact"
                variant="outlined"
                hide-details
                placeholder="Enter forwarding number"
                @keypress="onlyNumbers"
                @keyup.enter="saveJailForwardingNumber"
                @keyup.esc="cancelJailForwardingEdit"
                ref="jailForwardingInput"
              ></v-text-field>
              <v-icon 
                class="ms-2" 
                color="success" 
                size="small"
                @click="saveJailForwardingNumber"
              >
                mdi-check
              </v-icon>
              <v-icon 
                class="ms-2" 
                color="error" 
                size="small"
                @click="cancelJailForwardingEdit"
              >
                mdi-close
              </v-icon>
            </div>
          </div>
          <v-radio value="normal">
            <template v-slot:label>
              <span style="font-size: 17px; font-weight: bold; color: #424242;">Normal call handling using the Jail Call intake menu</span>
            </template>
          </v-radio>
        </v-radio-group>
      </div>
      
      <!-- Non-Jail Calls Section -->
      <div class="text-h6 font-weight-bold mt-6" style="color: #424242;">
        When a Bail call comes in:
      </div>
      <div class="radio-group mt-4 ms-8">
        <v-radio-group 
          v-model="bailCallHandling"
          @update:model-value="handleBailCallHandlingChange"
        >
          <v-radio value="forward">
            <template v-slot:label>
              <div class="d-flex align-items-center" style="font-size: 17px; font-weight: bold; color: #424242;">
                Forward it to this number:
              </div>
            </template>
          </v-radio>
          <div class="ms-8 mt-2 mb-4 d-flex align-center" :class="{ 'opacity-50': bailCallHandling !== 'forward' }">
            <!-- View Mode -->
            <div v-if="!isEditingBailForwarding" class="d-flex align-center">
              <div class="uneditable-number">{{ bailForwardingNumber || 'No number set' }}</div>
              <v-icon 
                class="ms-2 edit-icon" 
                size="small"
                @click="startBailCallForwardingEdit"
              >
                mdi-pencil
              </v-icon>
            </div>

            <!-- Edit Mode -->
            <div v-else class="d-flex align-center">
              <v-text-field
                v-model="bailEditingNumber"
                class="forwarding-input"
                density="compact"
                variant="outlined"
                hide-details
                placeholder="Enter forwarding number"
                @keypress="onlyNumbers"
                @keyup.enter="saveBailForwardingNumber"
                @keyup.esc="cancelBailForwardingEdit"
                ref="bailForwardingInput"
              ></v-text-field>
              <v-icon 
                class="ms-2" 
                color="success" 
                size="small"
                @click="saveBailForwardingNumber"
              >
                mdi-check
              </v-icon>
              <v-icon 
                class="ms-2" 
                color="error" 
                size="small"
                @click="cancelBailForwardingEdit"
              >
                mdi-close
              </v-icon>
            </div>
          </div>
          <v-radio value="voicemail">
            <template v-slot:label>
              <span style="font-size: 17px; font-weight: bold; color: #424242;">Send to Voicemail</span>
            </template>
          </v-radio>
        </v-radio-group>
      </div>

      <!-- Voicemail Prompt Section -->
      <div class="text-h6 font-weight-bold mt-6" style="color: #424242;">
        Edit Your Voicemail (non-jail call) Greeting:
      </div>
      <div class="ms-8 mt-4 vm-prompt">
        <!-- Edit Mode -->
        <div v-if="editingVoicemail" class="d-flex flex-column">
          <v-row no-gutters>
            <v-col cols="11">
              <v-textarea
                v-model="editingVoicemailText"
                density="compact"
                variant="outlined"
                hide-details
                :rows="4"
                class="flex-grow-1"
                @keyup.esc="cancelVoicemailEdit"
                ref="voicemailInput"
              ></v-textarea>
            </v-col>
            <v-col cols="1" class="d-flex justify-center">
              <div class="d-flex flex-column ms-2">
                <v-icon 
                  color="success" 
                  size="small"
                  class="mb-2"
                  @click="saveVoicemailEdit"
                >
                  mdi-check
                </v-icon>
                <v-icon 
                  color="error" 
                  size="small"
                  @click="cancelVoicemailEdit"
                >
                  mdi-close
                </v-icon>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- View Mode -->
        <v-row v-else no-gutters>
          <v-col cols="11">
            <div class="voicemail-text">{{ voicemailPrompt['en-US'] }}</div>
          </v-col>
          <v-col cols="1" class="d-flex justify-center">
            <v-icon 
              class="ms-2 edit-icon" 
              size="small"
              @click="startVoicemailEdit"
            >
              mdi-pencil
            </v-icon>
          </v-col>
        </v-row>

        <!-- Translations -->
        <div class="translation-container mt-2">
          <v-row no-gutters>
            <v-col cols="11">
              <div class="translation-item">
                <v-btn
                  v-if="showVoicemailTranslateButton"
                  class="translation-button"
                  color="#d2b48c"
                  variant="flat"
                  @click="handleVoicemailTranslate" 
                >
                  Trans-<br>late
                </v-btn>
                <div class="translation-text">{{ voicemailPrompt['es-MX'] }}</div>
              </div>
            </v-col>
          </v-row>
          <v-row no-gutters class="mt-2">
            <v-col cols="11">
              <div class="translation-item">
                <div class="translation-text">{{ voicemailPrompt['ru-RU'] }}</div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue';
import axios from 'axios';

export default {
  name: 'VoicemailForwarding',
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  emits: ['update:company', 'show-dialog'],
  setup(props, { emit }) {
    // State
    const bailCallHandling = ref(props.company?.nonJailCallHandling || 'voicemail');
    const bailEditingNumber = ref(props.company?.forwardingNumber || '');
    const bailForwardingInput = ref(null);
    const bailForwardingNumber = ref(props.company?.forwardingNumber || '');
    const editingVoicemail = ref(false);
    const editingVoicemailText = ref('');
    const voicemailInput = ref(null);
    const voicemailPrompt = ref(
      props.company?.voicemailPrompts || 
      { 'en-US': '', 'es-MX': '', 'ru-RU': '' }
    );
    const showVoicemailTranslateButton = ref(false);
    const isEditingBailForwarding = ref(false);
    
    // Jail call handling state
    const jailCallHandling = ref(props.company?.jailCallHandling || 'normal');
    const jailEditingNumber = ref(props.company?.jailCallForwardingNumber || '');
    const jailForwardingInput = ref(null);
    const jailForwardingNumber = ref(props.company?.jailCallForwardingNumber || '');
    const isEditingJailForwarding = ref(false);

    // Input validation
    const onlyNumbers = (event) => {
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault();
      }
    };

    // Jail call forwarding methods
    const startJailCallForwardingEdit = async () => {
      isEditingJailForwarding.value = true;
      jailEditingNumber.value = jailForwardingNumber.value;
      await nextTick();
      jailForwardingInput.value?.focus();
    };

    const saveJailForwardingNumber = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { jailCallForwardingNumber: jailEditingNumber.value }
        );

        if (response.data.success) {
          emit('update:company', {
            jailCallForwardingNumber: jailEditingNumber.value
          });
          jailForwardingNumber.value = jailEditingNumber.value;
          isEditingJailForwarding.value = false;
          emit('show-dialog', 'Your jail call forwarding number has been saved.');
        } else {
          emit('show-dialog', 'There was an error saving your change. Please try again.');
        }
      } catch (error) {
        console.error('Error saving jail forwarding number:', error);
        emit('show-dialog', 'There was an error saving your change. Please try again.');
      }
    };

    const cancelJailForwardingEdit = () => {
      jailEditingNumber.value = jailForwardingNumber.value;
      isEditingJailForwarding.value = false;
    };

    // Jail call handling methods
    const handleJailCallHandlingChange = async (newValue) => {
      // Cancel forwarding edit mode if switching to normal
      if (newValue === 'normal' && isEditingJailForwarding.value) {
        cancelJailForwardingEdit();
      }

      // If switching to forward and no number is set, show message but allow the change
      if (newValue === 'forward' && (!jailForwardingNumber.value || jailForwardingNumber.value.trim() === '')) {
        emit('show-dialog', 'Enter a jail call forwarding phone number in the input box.');
        return; // Don't make the API call yet - wait for them to enter and save a number
      }

      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { jailCallHandling: newValue }
        );

        if (response.data.success) {
          emit('update:company', { jailCallHandling: newValue });
          
          const message = newValue === 'forward' 
            ? `Jail calls will be forwarded to: ${jailForwardingNumber.value}.`
            : 'Jail calls will be handled using the Jail Call intake menu.';
          
          emit('show-dialog', message);
        } else {
          jailCallHandling.value = props.company?.jailCallHandling || 'normal';
          emit('show-dialog', 'There was an error saving your change. Please try again.');
        }
      } catch (error) {
        console.error('Error saving jail call handling:', error);
        jailCallHandling.value = props.company?.jailCallHandling || 'normal';
        emit('show-dialog', 'There was an error saving your change. Please try again.');
      }
    };

    // Forwarding number methods
    const startBailCallForwardingEdit = async () => {
      isEditingBailForwarding.value = true;
      bailEditingNumber.value = bailForwardingNumber.value;
      await nextTick();
      bailForwardingInput.value?.focus();
    };

    const saveBailForwardingNumber = async () => {
      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { forwardingNumber: bailEditingNumber.value }
        );

        if (response.data.success) {
          emit('update:company', {
            forwardingNumber: bailEditingNumber.value
          });
          bailForwardingNumber.value = bailEditingNumber.value;
          isEditingBailForwarding.value = false;
          emit('show-dialog', 'Your forwarding number has been saved.');
        } else {
          emit('show-dialog', 'There was an error saving your change. Please try again.');
        }
      } catch (error) {
        console.error('Error saving forwarding number:', error);
        emit('show-dialog', 'There was an error saving your change. Please try again.');
      }
    };

    const cancelBailForwardingEdit = () => {
      bailEditingNumber.value = bailForwardingNumber.value;
      isEditingBailForwarding.value = false;
    };

    // Call handling methods
    const handleBailCallHandlingChange = async (newValue) => {
      // Cancel forwarding edit mode if switching to voicemail
      if (newValue === 'voicemail' && isEditingBailForwarding.value) {
        cancelBailForwardingEdit();
      }

      // If switching to forward and no number is set, show message but allow the change
      if (newValue === 'forward' && (!bailForwardingNumber.value || bailForwardingNumber.value.trim() === '')) {
        emit('show-dialog', 'Enter a forwarding phone number in the input box.');
        return; // Don't make the API call yet - wait for them to enter and save a number
      }

      try {
        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { nonJailCallHandling: newValue }
        );

        if (response.data.success) {
          emit('update:company', { nonJailCallHandling: newValue });
          
          const message = newValue === 'forward' 
            ? `NON-jail calls will be forwarded to: ${bailForwardingNumber.value}.`
            : 'NON-jail calls will be sent to Voicemail.';
          
          emit('show-dialog', message);
        } else {
          bailCallHandling.value = props.company?.nonJailCallHandling || 'voicemail';
          emit('show-dialog', 'There was an error saving your change. Please try again.');
        }
      } catch (error) {
        console.error('Error saving call handling:', error);
        bailCallHandling.value = props.company?.nonJailCallHandling || 'voicemail';
        emit('show-dialog', 'There was an error saving your change. Please try again.');
      }
    };

    // Voicemail methods
    const startVoicemailEdit = async () => {
      editingVoicemail.value = true;
      editingVoicemailText.value = voicemailPrompt.value['en-US'];
      await nextTick();
      voicemailInput.value?.focus();
    };

    const cancelVoicemailEdit = () => {
      editingVoicemail.value = false;
      editingVoicemailText.value = '';
    };

    const saveVoicemailEdit = async () => {
      try {
        const newPrompts = {
          'en-US': editingVoicemailText.value,
          'es-MX': '',
          'ru-RU': ''
        };

        const response = await axios.put(
          `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
          { voicemailPrompts: newPrompts }
        );

        if (response.data.success) {
          voicemailPrompt.value = newPrompts;
          showVoicemailTranslateButton.value = true;
          editingVoicemail.value = false;
          editingVoicemailText.value = '';
          
          emit('update:company', {
            voicemailPrompts: newPrompts
          });

          emit('show-dialog', 'Your voicemail prompt has been updated.');
        } else {
          emit('show-dialog', 'There was an error saving your changes. Please try again.');
        }
      } catch (error) {
        console.error('Error saving voicemail prompt:', error);
        emit('show-dialog', 'There was an error saving your changes. Please try again.');
      }
    };

    const handleVoicemailTranslate = async () => {
      try {
        const response = await axios.post('/portaldb/db/translateMenuItem', {
          English: voicemailPrompt.value['en-US']
        });

        if (response.data) {
          const newPrompts = {
            'en-US': voicemailPrompt.value['en-US'],
            'es-MX': response.data['es-MX'],
            'ru-RU': response.data['ru-RU']
          };

          const updateResponse = await axios.put(
            `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
            { voicemailPrompts: newPrompts }
          );

          if (updateResponse.data.success) {
            voicemailPrompt.value = newPrompts;
            showVoicemailTranslateButton.value = false;

            emit('update:company', {
              voicemailPrompts: newPrompts
            });

            emit('show-dialog', 'Your translation was successfully saved.');
          } else {
            emit('show-dialog', 'There was an error saving the translations. Please try again.');
          }
        } else {
          emit('show-dialog', 'Translation failed. Please try again.');
        }
      } catch (error) {
        console.error('Translation error:', error);
        emit('show-dialog', 'An error occurred during translation. Please try again.');
      }
    };

    // Lifecycle hooks
    onMounted(async () => {
      const hasSpanish = voicemailPrompt.value['es-MX']?.trim();
      const hasRussian = voicemailPrompt.value['ru-RU']?.trim();
      showVoicemailTranslateButton.value = !hasSpanish || !hasRussian;
      
      // Initialize default values for jail call properties if they don't exist
      const defaultsToSet = {};
      let needToUpdateDefaults = false;
      
      if (props.company.jailCallHandling === undefined) {
        defaultsToSet.jailCallHandling = 'normal';
        jailCallHandling.value = 'normal';
        needToUpdateDefaults = true;
      }
      
      if (props.company.jailCallForwardingNumber === undefined) {
        defaultsToSet.jailCallForwardingNumber = '';
        jailForwardingNumber.value = '';
        needToUpdateDefaults = true;
      }
      
      // Persist default values to database if needed
      if (needToUpdateDefaults) {
        try {
          const response = await axios.put(
            `/portaldb/db/updateCompanyProperties/${props.company.companyNumber}`,
            defaultsToSet
          );
          
          if (response.data.success) {
            emit('update:company', defaultsToSet);
            console.log('Default jail call properties initialized');
          } else {
            console.error('Failed to initialize default jail call properties');
          }
        } catch (error) {
          console.error('Error initializing default jail call properties:', error);
        }
      }
    });

    return {
      bailCallHandling,
      bailEditingNumber,
      bailForwardingInput,
      bailForwardingNumber,
      editingVoicemail,
      editingVoicemailText,
      voicemailInput,
      voicemailPrompt,
      showVoicemailTranslateButton,
      isEditingBailForwarding,
      onlyNumbers,
      saveBailForwardingNumber,
      cancelBailForwardingEdit,
      handleBailCallHandlingChange,
      startVoicemailEdit,
      cancelVoicemailEdit,
      saveVoicemailEdit,
      handleVoicemailTranslate,
      startBailCallForwardingEdit,
      // Jail call handling
      jailCallHandling,
      jailEditingNumber,
      jailForwardingInput,
      jailForwardingNumber,
      isEditingJailForwarding,
      startJailCallForwardingEdit,
      saveJailForwardingNumber,
      cancelJailForwardingEdit,
      handleJailCallHandlingChange
    };
  }
};
</script>

<style scoped>
.accordion-container {
  background-color: #f5f5dc;
  padding: 20px;
  border-radius: 8px;
}

.prompts-container {
  min-height: 400px;
}

.voicemail-text, 
.translation-text {
  min-height: 120px;
  white-space: pre-wrap;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  background-color: white;
}

.translation-container {
  padding-left: 0;
  width: 100%;
  position: relative;
}

.translation-button {
  position: absolute !important;
  left: -93px !important;
  top: 8px !important;
  transform: none !important;
  height: 50px !important;
  min-width: 32px !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
  padding: 4px !important;
  z-index: 1;
}

.translation-item {
  width: 100%;
  position: relative;
}

.edit-icon {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.edit-icon:hover {
  opacity: 1;
}

:deep(.v-textarea .v-field__input) {
  min-height: 120px !important;
  white-space: pre-wrap;
  width: 100%;
}

.uneditable-number {
  padding: 8px 12px;
  border-radius: 4px;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.38);
  width: 200px;  /* Set fixed width */
}

:deep(.forwarding-input) {
  width: 200px;
}

</style>
